import React from 'react';
import { Link } from 'react-router-dom';
import { stringify } from 'query-string';

import { ZAPPOS_MERCHANTID } from 'constants/crossSiteMerchantIdMapping';
import { IS_GIFT_CARD } from 'common/regex';
import { ORDER_HISTORY_ACTION_TYPE, ORDER_LINK_TYPE, PRODUCT_LINK_TYPE } from 'constants/amethystEnums';
import { ORDER_HISTORY_PAGE } from 'constants/amethystPageTypes';
import {
  applyReturnTrackingStatusToCompositeStatus,
  generateProductUrl,
  getLegacyStatusFromCompositeStatus,
  hasShipmentBeenDelivered,
  isItemCancellable
} from 'helpers/MyAccountUtils';
import ShippingStatus from 'components/account/ShippingStatus';
import useMartyContext from 'hooks/useMartyContext';
import { evOrderHistoryActionClick, evOrderHistoryCancelReturnClick, evOrderHistoryTrackingClick, evProductClickThrough } from 'events/account';
import { getAmethystPageType } from 'helpers/analytics';
import StarRatingRadio from 'components/common/StarRatingRadio';
import MarketplaceAwareLink from 'components/common/MarketplaceAwareLink';
import { evWriteProductReviewClick } from 'events/productReview';
import ReturnsUtils from 'helpers/ReturnsUtils';
import { cn } from 'helpers/classnames';
import { useFlag } from 'containers/UnleashFlagProvider';
import { FEATURE_EXCHANGES_DISABLE } from 'constants/features';
import GiftWrapIndicator from 'components/account/GiftWrapIndicator';

import css from 'styles/components/account/orderHistory/orderHistoryTableExpandedItemRow.scss';

const { TEXT_LINK } = PRODUCT_LINK_TYPE;
const { ORDER_IMAGE_LINK } = ORDER_LINK_TYPE;

export const OrderHistoryTableExpandedItemRow = ({
  item,
  orderId,
  pageType,
  orderLink,
  index,
  handleOrderNumberClick,
  orderItemCount,
  orderIndex,
  useNewCancellableFlag
}) => {
  const { amethystTrack, testId, marketplace, router } = useMartyContext();
  const {
    id,
    merchantId,
    itemUniqueId,
    returnable,
    returnLabelUrl,
    returnDetails = {},
    exchangeable,
    legacy,
    product,
    shipment,
    amount,
    giftWrapped,
    giftMessage,
    giftWrapCharge,
    compositeStatus: extractedCompositeStatus,
    reviewed,
    egcInfo: { emailAddress, deliveryDisplayDate, message, from } = {}
  } = item;
  const { attributes = [], retinaImageUrl, name, brand, style, reviewUrl, productId, styleId, stockId, asin } = product;
  const { shipmentDisplayMessage, id: shipmentId, trackingAvailable, compositeStatus: shipmentCompositeStatus } = shipment;
  const {
    dataMaskPiiElements,
    account: { showReturnLabelLink },
    features: { showReviews: reviewsEnabled },
    checkout: { asinVerbiage = 'Asin' },
    returns: { enableCancelReturn, showImprovedReturnTrackingStatus }
  } = marketplace;
  const { cancellable: isReturnCancellable, contractUnitId, planName, returnTrackingStatus } = returnDetails || {};
  const isGiftCard = IS_GIFT_CARD.test(brand);
  const productUrl = generateProductUrl(product, legacy);
  const amethystReviewClick = () => [evWriteProductReviewClick, { productId, styleId, stockId, asin, addedFrom: ORDER_HISTORY_PAGE }];
  const compositeStatus = showImprovedReturnTrackingStatus
    ? applyReturnTrackingStatusToCompositeStatus(extractedCompositeStatus, returnTrackingStatus)
    : extractedCompositeStatus;
  const isReturnWithQRCode = ReturnsUtils.isReturnWithQRCode(planName);
  const isUpsPickupReturn = planName?.includes('Pickup');
  const returnLink = `${orderLink}&${stringify({
    preselectedItems: itemUniqueId,
    returnMethod: 'return'
  })}`;
  const exchangeLink = `${orderLink}&${stringify({
    preselectedItems: itemUniqueId,
    returnMethod: 'exchange'
  })}`;
  const showReviewStars =
    !reviewed &&
    reviewUrl &&
    merchantId === ZAPPOS_MERCHANTID &&
    hasShipmentBeenDelivered(getLegacyStatusFromCompositeStatus(shipmentCompositeStatus)); // only enable review stars for zappos line items
  const cancelReturnUrl = ReturnsUtils.getCancelReturnUrl(orderId, contractUnitId && [contractUnitId], isUpsPickupReturn);
  const showCancelReturnLink = isReturnCancellable && enableCancelReturn && cancelReturnUrl;
  const showCancelItemLink = isItemCancellable(item, useNewCancellableFlag);
  const showGiftWrapIndicator = giftWrapped || giftMessage;
  // Feature flag to disable exchanges
  const isExchangesDisabled = useFlag(FEATURE_EXCHANGES_DISABLE);

  const onCancelReturnClick = e => {
    const { orderHistoryActionType } = e.currentTarget.dataset;
    amethystTrack(() => [
      evOrderHistoryCancelReturnClick,
      {
        asin,
        orderHistoryActionType,
        planName,
        returnTrackingStatus,
        sourcePage: getAmethystPageType(pageType)
      }
    ]);
  };

  const onActionClick = e => {
    const { orderHistoryActionType } = e.currentTarget.dataset;
    amethystTrack(() => [
      evOrderHistoryActionClick,
      {
        orderHistoryActionType,
        product,
        sourcePage: getAmethystPageType(pageType)
      }
    ]);
  };

  const onProductClick = e => {
    const { productLinkType } = e.currentTarget.dataset;
    amethystTrack(() => [
      evProductClickThrough,
      {
        productLinkType,
        product,
        sourcePage: getAmethystPageType(pageType)
      }
    ]);
  };

  const onShipmentClick = () => {
    amethystTrack(() => [
      evOrderHistoryTrackingClick,
      {
        orderId,
        compositeStatus, // composite status of item not shipment
        shipmentId,
        sourcePage: getAmethystPageType(pageType)
      }
    ]);
  };

  return (
    <div data-test-id="orderHistoryTableExpandedItemRow" className={css.productRow}>
      <div className={css.imageColumn} data-cs-mask={dataMaskPiiElements}>
        {/* this link has the same behavior as images on the legacy table */}
        {/* this is firing the same event as the other order history links, the item-index represents individual items instead of order items in this case */}
        <Link
          aria-hidden={true}
          tabIndex={-1}
          to={orderLink || ''}
          className={css.mProduct}
          onClick={handleOrderNumberClick}
          data-link-type={ORDER_IMAGE_LINK}
          data-order-id={orderId}
          data-item-index={index}
          data-order-index={orderIndex}
          data-order-item-count={orderItemCount}
          data-test-id={testId('productImageLink')}
        >
          <div className={css.image}>
            <img src={retinaImageUrl} alt={name} />
          </div>
        </Link>
      </div>
      <div className={css.productInfoColumn} data-cs-mask={dataMaskPiiElements}>
        <div className={css.productAttributes}>
          <p className="zappos:body-m" data-test-id={testId('productBrand')}>
            {brand}
          </p>
          <MarketplaceAwareLink
            merchantId={merchantId}
            to={productUrl}
            onClick={onProductClick}
            data-product-link-type={TEXT_LINK}
            data-test-id={testId('productStyle')}
            className={cn(css.productAttributeLink, 'zappos:body-m-bold')}
          >
            {style}
          </MarketplaceAwareLink>
          <dl>
            {!isGiftCard &&
              attributes.map(({ key, value }) => (
                <div className="zappos:body-m" key={`${key}-${value}`}>
                  <dt>{key === 'Asin' ? asinVerbiage : key}</dt>
                  <dd data-test-id={testId(`attribute-${key}`)}>{value}</dd>
                </div>
              ))}
            {message && (
              <dl className="flex flex-wrap">
                <dt className="zappos:body-m">Message</dt>
                <dd className="m-0 zappos:body-m" data-test-id={testId('attribute-message')}>
                  {message}
                </dd>
              </dl>
            )}
            {emailAddress && (
              <div className="flex flex-wrap">
                <dt className="zappos:body-m">Email To</dt>
                <dd className="m-0 zappos:body-m" data-test-id={testId('attribute-email')}>
                  {emailAddress}
                </dd>
              </div>
            )}
            {from && (
              <div className="flex flex-wrap">
                <dt className="zappos:body-m">From</dt>
                <dd className="m-0 zappos:body-m" data-test-id={testId('attribute-from')}>
                  {from}
                </dd>
              </div>
            )}
            {deliveryDisplayDate && (
              <div className="flex flex-wrap">
                <dt className="zappos:body-m">Deliver On</dt>
                <dd className="m-0 zappos:body-m" data-test-id={testId('attribute-delivery-date')}>
                  {deliveryDisplayDate}
                </dd>
              </div>
            )}
            <div className="zappos:body-m">
              <dt>Price</dt>
              <dd className={cn(css.orderPrice, 'zappos:body-m')}>{amount}</dd>
            </div>
          </dl>
        </div>
      </div>
      {reviewsEnabled && (
        <div className={css.starsColumn}>
          {showReviewStars && (
            <div className={cn(css.starsContainer, 'zappos:body-m')}>
              Overall, how was this product?
              <StarRatingRadio
                containerClassName={css.stars}
                onChange={e => {
                  router.pushPreserveAppRoot(`${reviewUrl}?rating=${e.target.value}`);
                  amethystTrack(amethystReviewClick);
                }}
              />
            </div>
          )}
        </div>
      )}
      <div data-test-id={testId('orderStatus')} className={css.shippingStatusColumn}>
        <ShippingStatus
          orderId={orderId}
          shipmentId={shipmentId}
          status={getLegacyStatusFromCompositeStatus(compositeStatus)}
          shipmentMessage={shipmentDisplayMessage}
          hideTrackingProgress={true}
          trackingAvailable={trackingAvailable}
          onShipmentClick={onShipmentClick}
          planName={planName}
        />
      </div>
      <div className={css.actionsColumn} data-cs-mask={dataMaskPiiElements}>
        {returnable && (
          <Link
            to={returnLink}
            className={cn(css.actionLink, 'zappos:body-m zappos:!font-medium')}
            data-order-history-action-type={ORDER_HISTORY_ACTION_TYPE.ORDER_HISTORY_RETURN}
            onClick={onActionClick}
          >
            Return Item
          </Link>
        )}
        {exchangeable && !isExchangesDisabled && (
          <Link
            to={exchangeLink}
            className={cn(css.actionLink, 'zappos:body-m zappos:!font-medium')}
            data-order-history-action-type={ORDER_HISTORY_ACTION_TYPE.ORDER_HISTORY_EXCHANGE}
            onClick={onActionClick}
          >
            Exchange Item
          </Link>
        )}
        {!isUpsPickupReturn && showReturnLabelLink && returnLabelUrl && (
          <Link
            to={`${returnLabelUrl}&displayInfo=true`}
            target="_blank"
            className={cn(css.actionLink, 'zappos:body-m zappos:!font-medium')}
            data-order-history-action-type={ORDER_HISTORY_ACTION_TYPE.PRINT_RETURN_LABEL}
            onClick={onActionClick}
          >
            {isReturnWithQRCode ? 'View Return Code' : 'Print Return Label'}
          </Link>
        )}
        {showCancelItemLink && (
          <Link
            to={`/orders/${orderId}/cancel?lineItemId=${id}`}
            className={cn(css.actionLink, 'zappos:body-m zappos:!font-medium')}
            data-order-history-action-type={ORDER_HISTORY_ACTION_TYPE.CANCEL_ITEM}
            onClick={onActionClick}
          >
            Cancel Item
          </Link>
        )}
        {showCancelReturnLink && (
          <Link
            to={cancelReturnUrl}
            className={cn(css.actionLink, 'zappos:body-m zappos:!font-medium')}
            data-order-history-action-type={ORDER_HISTORY_ACTION_TYPE.ORDER_HISTORY_CANCEL_RETURN}
            onClick={onCancelReturnClick}
          >
            {' '}
            Cancel Return{' '}
          </Link>
        )}
      </div>
      {showGiftWrapIndicator && (
        <GiftWrapIndicator giftWrapped={giftWrapped} giftMessage={giftMessage} giftWrapCharge={giftWrapCharge} className={css.giftWrapRow} />
      )}
    </div>
  );
};

export default OrderHistoryTableExpandedItemRow;
